// @ds/config/buildEntityConfig — assemble an `EntityConfig<T>` from the presentation
// layer.
//
// `EntityConfig<T>` is the page contract consumed by the shells (EntityListPage /
// EntityViewPage / DataTable). This builder is the single canonical place that
// projects the presentation layer (definePresentation fields + PresentationConfig
// placement + authored chrome) into that shape: schema (fields + __meta), list
// (columns with placement), view (sections + chrome).
//
// Hard rules (so it stays honest):
//   - Downstream-only. Nothing upstream is shaped to suit this file. If a field
//     can't be expressed without adding a knob to PresentationField, STOP and
//     fix the model — do not grow PresentationField for the builder's sake.
//   - Pure projection. No new rendering logic, no behavior forks.

import type { ReactNode } from "react";
import type { EntityConfig } from "@ds/config/entityConfig";
import type { ListConfig, ViewConfig } from "@ds/config/pageConfig";
import type { ActionSpec, SectionDef } from "@ds/config/sectionConfig";
import type { Schema, SchemaField, SchemaMeta } from "@ds/config/schema";
import type { ListColumnSpec } from "@ds/tables/types";
import type { TFunction } from "i18next";
import type { PresentationSchema } from "@ds/config/presentation";
import { toListColumn as toField } from "@ds/config/presentationRefs";
import type { PresentationConfig } from "@ds/config/presentationConfig";

// ─── Field: PresentationField → SchemaField (FieldDef + key + deps + edit) ──
// (`toField` / `toDeps` are the shared conversion path in @ds/config/presentationRefs.)

// ─── Schema map (+ __meta) ──────────────────────────────────────────────────

function toSchema<T>(
  presentation: PresentationSchema,
  meta: SchemaMeta,
): { schema: Schema<T>; fieldsByKey: Record<string, ListColumnSpec> } {
  const fieldsByKey: Record<string, ListColumnSpec> = {};
  const out: Record<string, SchemaField<T, unknown>> = {};
  for (const key of Object.keys(presentation)) {
    const pf = presentation[key];
    if (!pf) continue;
    const f = toField(pf);
    fieldsByKey[key] = f;
    out[key] = f as unknown as SchemaField<T, unknown>;
  }
  Object.defineProperty(out, "__meta", { value: meta, enumerable: false });
  return { schema: out as unknown as Schema<T>, fieldsByKey };
}

// ─── List + View ────────────────────────────────────────────────────────────

/** List-page chrome the presentation layers don't model (authored, passed through). */
export interface ListExtras<T = unknown> {
  searchEntity?: string;
  /** Data-fetch mode. `"client"` fetches the whole set once (`pagination:
   *  off`) and filters/sorts/searches client-side — for small dictionaries
   *  (≤ hundreds of rows) where every row should be on screen. Default
   *  `"server"` (paginated). */
  mode?: "server" | "client";
  defaultSort?: { field: string; order: "asc" | "desc" }[];
  title?: (t: TFunction) => string;
  breadcrumb?: (t: TFunction) => { label: string }[];
  splitPaneKey?: string;
  /** Entity create (declarative SchemaForm or bespoke render). */
  create?: ListConfig<T>["create"];
  /** Toolbar quick / advanced filters BEYOND the schema-auto set (cross-field,
   *  computed, or bespoke-component filters). Schema-field filters auto-derive. */
  quickFilters?: ListConfig<T>["quickFilters"];
  advancedFilters?: ListConfig<T>["advancedFilters"];
  /** Columns always fetched even when no visible field references them. */
  permanentFields?: ListConfig<T>["permanentFields"];
}

function toList<T>(
  config: PresentationConfig,
  fieldsByKey: Record<string, ListColumnSpec>,
  extras: ListExtras<T> = {},
): ListConfig<T> {
  const columns = config.list.columns
    .map((c) => {
      const field = fieldsByKey[c.key];
      if (!field) return null;
      return {
        ...field,
        ...(c.width !== undefined ? { width: c.width } : {}),
        ...(c.pinned !== undefined ? { pinned: c.pinned } : {}),
        ...(c.defaultVisible !== undefined ? { defaultVisible: c.defaultVisible } : {}),
        ...(c.align !== undefined ? { align: c.align } : {}),
        ...(c.hideable !== undefined ? { hideable: c.hideable } : {}),
        ...(c.sorter !== undefined ? { sorter: c.sorter } : {}),
      } as ListColumnSpec<T>;
    })
    .filter((c): c is ListColumnSpec<T> => !!c);

  // NOTE: do NOT pass `advancedFilters` here. EntityListPage auto-derives the
  // real FilterDefs from `schema.<field>.filterable` (via schemaFilters); passing
  // string keys would be normalised to text filters and OVERRIDE those (their
  // names collide), turning every filter into a text box.
  return {
    layout: { kind: "table", mode: extras.mode ?? "server", columns },
    ...(extras.searchEntity ? { searchEntity: extras.searchEntity } : {}),
    ...(extras.defaultSort ? { defaultSort: extras.defaultSort } : {}),
    ...(extras.title ? { title: extras.title } : {}),
    ...(extras.breadcrumb ? { breadcrumb: extras.breadcrumb } : {}),
    ...(extras.create ? { create: extras.create } : {}),
    ...(extras.quickFilters ? { quickFilters: extras.quickFilters } : {}),
    ...(extras.advancedFilters ? { advancedFilters: extras.advancedFilters } : {}),
    ...(extras.permanentFields ? { permanentFields: extras.permanentFields } : {}),
  } as ListConfig<T>;
}

interface AdapterMeta<T = unknown> extends SchemaMeta {
  /** Detail-page title; defaults to `record.name ?? #id`. */
  title?: (record: T, t: TFunction) => string;
}

/** An authored KV section referencing field KEYS (resolved to the converted
 *  fields). Anything else (data sub-tables, custom) is a raw SectionDef. */
export interface RecordSectionSpec {
  kind: "record";
  key?: string;
  /** Restrict to one page chrome — see `BaseSection.chromeOnly`. */
  chromeOnly?: "classic" | "document";
  title?: SectionDef<unknown>["title"];
  column?: "main" | "side";
  tabKey?: string;
  tabLabel?: SectionDef<unknown>["tabLabel"];
  /** Conditional rendering predicate(s) on the record. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  when?: (record: any) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  visible?: (record: any) => boolean;
  /** Record layout. Default `"kv-grid"`; `"cards"` for party-card fields. */
  layout?: "kv-grid" | "cards";
  /** Field keys rendered in the layout (in order). */
  fieldKeys: string[];

  // ── Chrome slots forwarded verbatim to the resolved SectionDef ──────────
  // A KV section is still a section: it may carry a header action (companies'
  // "Pokaż na mapie" sits on the address block), the state that action drives,
  // and the modal that state opens. `RecordSectionRenderer` already resolves
  // all three — without the passthrough the only way to reach them was to
  // demote the section to a hand-written `SectionDef`, losing `fieldKeys`.
  /** Section-scoped state hook — threaded into `actions` / `status` / `outside`. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useSectionState?: (record: any) => any;
  /** Header action buttons. */
  actions?:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | ActionSpec<any, any>[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | ((record: any, state: any, t: TFunction) => ActionSpec<any, any>[]);
  /** Header right slot (status pill). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  status?: (record: any, state: any) => ReactNode;
  /** Sibling render slot — modals/drawers that must escape the Card's overflow. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  outside?: (record: any, state: any) => ReactNode;
}

/** Authored view chrome the generator can't derive — passed through verbatim. */
export interface ViewChrome<T> {
  crud?: ViewConfig<T>["crud"];
  title?: ViewConfig<T>["title"];
  subtitle?: ViewConfig<T>["subtitle"];
  statusPills?: ViewConfig<T>["statusPills"];
  statBand?: ViewConfig<T>["statBand"];
  banner?: ViewConfig<T>["banner"];
  keyFacts?: ViewConfig<T>["keyFacts"];
  breadcrumb?: ViewConfig<T>["breadcrumb"];
  tabs?: ViewConfig<T>["tabs"];
  bodyTemplate?: ViewConfig<T>["bodyTemplate"];
  chrome?: ViewConfig<T>["chrome"];
  headerControls?: ViewConfig<T>["headerControls"];
  eyebrow?: ViewConfig<T>["eyebrow"];
  meta?: ViewConfig<T>["meta"];
  /**
   * Groups for the document's `Dane` block, declared as FIELD KEYS. Resolved
   * against the same `fieldsByKey` map every section uses, so `Dane` shares
   * one accessor / formatter / edit block with the document above it instead
   * of restating values.
   */
  propertyGroups?: Array<{ title: ReactNode; fieldKeys: string[] }>;
  aggregates?: ViewConfig<T>["aggregates"];
  /** When present, REPLACES the generated KV sections. Record specs reference
   *  field keys; raw SectionDefs (data sub-tables, custom) pass through. */
  sections?: Array<RecordSectionSpec | SectionDef<T>>;
}

function toView<T>(
  config: PresentationConfig,
  fieldsByKey: Record<string, ListColumnSpec>,
  meta: AdapterMeta<T>,
  chrome: ViewChrome<T> = {},
): ViewConfig<T> {
  const resolveRecordSpec = (s: RecordSectionSpec): SectionDef<T> =>
    ({
      kind: "record",
      key: s.key,
      title: s.title,
      column: s.column,
      tabKey: s.tabKey,
      tabLabel: s.tabLabel,
      ...(s.when ? { when: s.when } : {}),
      ...(s.visible ? { visible: s.visible } : {}),
      ...(s.chromeOnly ? { chromeOnly: s.chromeOnly } : {}),
      ...(s.useSectionState ? { useSectionState: s.useSectionState } : {}),
      ...(s.actions ? { actions: s.actions } : {}),
      ...(s.status ? { status: s.status } : {}),
      ...(s.outside ? { outside: s.outside } : {}),
      layout: {
        kind: s.layout ?? "kv-grid",
        fields: s.fieldKeys.map((k) => fieldsByKey[k]).filter(Boolean),
      },
    }) as unknown as SectionDef<T>;

  const sections: SectionDef<T>[] = chrome.sections
    ? chrome.sections.map((s) =>
        "fieldKeys" in s ? resolveRecordSpec(s) : s,
      )
    : config.view.sections.map(
        (s) =>
          ({
            kind: "record",
            title: s.title,
            layout: { kind: "kv-grid", fields: s.fields.map((k) => fieldsByKey[k]).filter(Boolean) },
          }) as unknown as SectionDef<T>,
      );

  const title =
    chrome.title ??
    meta.title ??
    ((r: { name?: string; id?: number | string }) => r.name ?? `#${r.id}`);

  return {
    sections,
    title,
    ...(chrome.crud ? { crud: chrome.crud } : {}),
    ...(chrome.subtitle ? { subtitle: chrome.subtitle } : {}),
    ...(chrome.statusPills ? { statusPills: chrome.statusPills } : {}),
    ...(chrome.statBand ? { statBand: chrome.statBand } : {}),
    ...(chrome.banner ? { banner: chrome.banner } : {}),
    ...(chrome.keyFacts ? { keyFacts: chrome.keyFacts } : {}),
    ...(chrome.breadcrumb ? { breadcrumb: chrome.breadcrumb } : {}),
    ...(chrome.tabs ? { tabs: chrome.tabs } : {}),
    ...(chrome.bodyTemplate ? { bodyTemplate: chrome.bodyTemplate } : {}),
    ...(chrome.chrome     ? { chrome:     chrome.chrome }     : {}),
    ...(chrome.headerControls ? { headerControls: chrome.headerControls } : {}),
    ...(chrome.eyebrow    ? { eyebrow:    chrome.eyebrow }    : {}),
    ...(chrome.meta       ? { meta:       chrome.meta }       : {}),
    ...(chrome.propertyGroups
      ? {
          properties: chrome.propertyGroups.map((g) => ({
            title:  g.title,
            fields: g.fieldKeys.map((k) => fieldsByKey[k]).filter(Boolean),
          })),
        }
      : {}),
    ...(chrome.aggregates ? { aggregates: chrome.aggregates } : {}),
  } as ViewConfig<T>;
}

// ─── Entry point ────────────────────────────────────────────────────────────

/**
 * Assemble an `EntityConfig<T>` from the presentation schema + config.
 * `select` for the list is the schema-derived projection; placement comes from
 * the PresentationConfig; field display/edit/deps come from the schema.
 */
export function buildEntityConfig<T>(
  presentation: PresentationSchema,
  config: PresentationConfig,
  meta: AdapterMeta<T>,
  listExtras: ListExtras<T> = {},
  viewChrome: ViewChrome<T> = {},
): EntityConfig<T> {
  const { schema, fieldsByKey } = toSchema<T>(presentation, meta);
  return {
    schema,
    list: toList<T>(config, fieldsByKey, listExtras),
    view: toView<T>(config, fieldsByKey, meta, viewChrome),
    ...(listExtras.splitPaneKey ? { splitPaneKey: listExtras.splitPaneKey } : {}),
  };
}
